import Order, { OrderProduct } from "ts/api/order";
import { IRefund, RefundStatus } from "@evennt-tech/api-v2-core";
import { MoneyHelper, RefundCalculator } from "@evennt-tech/api-v2-payment";
import orderStatusesMap, { OrderStatusMap } from "tools/order/getOrderStatus";
import { getOrderTotalSupplierAmount } from "tools/order/getTotalOrderAmount";
import { getUniqueOrderFees } from "tools/order/getUniqueOrderFees";
import { ExtraProduct } from "ts/api/product";
import { Theme } from "@material-ui/core/styles";
import { useSuppliers } from "store/suppliers/supplier.store";

export const isOrderFullyRefunded = (order: Order, refunds: IRefund[]) => {
  const totalOrderQty = order.products.reduce(
    (acc, { quantity, extras = [] }) => {
      acc += quantity;
      for (const extra of extras) {
        acc += extra.quantity;
      }
      return acc;
    },
    0
  );

  const fees = getUniqueOrderFees(order.products);
  const totalFeesAmount = fees.reduce((acc, curr) => acc + curr.amount, 0);

  const { totalRefundedQty, totalRefundedFeesAmount } = refunds.reduce<{
    totalRefundedQty: number;
    totalRefundedFeesAmount: number;
  }>(
    (acc, { status, products = [], fees = [] }) => {
      const isRefundDone = [
        RefundStatus.Processed,
        RefundStatus.Processing,
      ].includes(status);
      if (!isRefundDone) return acc;
      for (const fee of fees) {
        acc.totalRefundedFeesAmount += fee.approvedAmount ?? 0;
      }
      for (const product of products) {
        acc.totalRefundedQty += product.approvedQuantity ?? 0;
      }

      return acc;
    },
    { totalRefundedQty: 0, totalRefundedFeesAmount: 0 }
  );

  return (
    MoneyHelper.to2dp(totalFeesAmount) ===
      MoneyHelper.to2dp(totalRefundedFeesAmount) &&
    totalRefundedQty === totalOrderQty
  );
};

const getFullProductAmount = (orderProduct: OrderProduct | ExtraProduct) => {
  return (
    (orderProduct.price - (orderProduct.discountAmount ?? 0)) *
    orderProduct.quantity
  );
};

type GetRefundStatusParams = {
  order: Order;
  refunds: IRefund[];
  palette?: Theme["palette"];
};

export const getProductRefundStatus = ({
  productId,
  refunds,
  order,
  palette,
  isExhibitorView,
}: GetRefundStatusParams & { productId: string; isExhibitorView: boolean }) => {
  const orderProduct = order.products.find((p) => p.id === productId);
  if (!orderProduct) {
    return null;
  }
  const productLineAmount = getFullProductAmount(orderProduct);
  const extrasAmount = (orderProduct.extras ?? []).reduce((acc, curr) => {
    return acc + getFullProductAmount(curr);
  }, 0);

  const totalOrderAmount = MoneyHelper.to2dp(productLineAmount + extrasAmount);

  const totalRefundedAmount = MoneyHelper.to2dp(
    refunds.reduce((acc, { status, products = [] }) => {
      if (status !== RefundStatus.Processed) return acc;

      const refundedAmount = products.reduce((productAcc, product) => {
        if (product.productId === productId) {
          productAcc +=
            (product.approvedAmount ?? 0) * (product.approvedQuantity ?? 0);
        }
        return productAcc;
      }, 0);

      return refundedAmount + acc;
    }, 0)
  );
  if (!totalRefundedAmount) {
    return orderStatusesMap.paid;
  }
  if (totalRefundedAmount >= totalOrderAmount) {
    if (isExhibitorView) {
      return { ...orderStatusesMap.refunded, shouldStrike: true };
    }
    return {
      ...orderStatusesMap.paid,
      shouldStrike: true,
      color: palette?.contrastText.main ?? "#000",
    };
  }
  return orderStatusesMap.partiallyRefunded;
};

export const getOrderRefundStatus = ({
  supplierCompanyId,
  refunds,
  order,
  productId,
  palette,
}: GetRefundStatusParams & {
  supplierCompanyId?: string;
  productId?: string;
}): OrderStatusMap | null => {
  if (!refunds.length) {
    return null;
  }

  const orderTotalAmount = getOrderTotalSupplierAmount(
    order,
    supplierCompanyId
  );
  const refundedAmount = RefundCalculator.getTotalRefundsAmount(refunds, false);

  const isFullyRefunded = orderTotalAmount === refundedAmount;
  if (isFullyRefunded) {
    return orderStatusesMap.cancelledAndRefunded;
  }
  const productStatus = productId
    ? getProductRefundStatus({
        order,
        refunds,
        productId,
        palette,
        isExhibitorView: !supplierCompanyId,
      })
    : null;
  const suppliers = useSuppliers.getState().items;
  const isRefundsEnabled = suppliers.some((s) => s.canApproveRefunds);

  const hasInReview =
    !!supplierCompanyId &&
    isRefundsEnabled &&
    refunds.some((r) => r.status === RefundStatus.InReview);
  const hasProcessing = refunds.some(
    (r) => r.status === RefundStatus.Processing
  );
  const hasProcessed = refunds.some((r) => r.status === RefundStatus.Processed);

  const isMixedStatus =
    [hasInReview, hasProcessed, hasProcessing].filter(Boolean).length > 1;
  if (isMixedStatus || hasProcessing) {
    if (productStatus) {
      return productStatus;
    }
    if (hasProcessing) {
      return orderStatusesMap.refundProcessing;
    }
    return orderStatusesMap.refundRequested;
  }
  if (hasInReview) {
    if (productStatus) {
      return productStatus;
    }
    return orderStatusesMap.refundRequested;
  }
  if (hasProcessed) {
    if (productStatus) {
      return productStatus;
    }
    return orderStatusesMap.partiallyRefunded;
  }

  return null;
};
